import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth, formatApiErrorDetail } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import SettingsGear from '../components/SettingsGear';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Mail, Lock, ArrowRight, ArrowLeft, KeyRound, Shield } from 'lucide-react';
import axios from 'axios';

const API_URL = window.location.hostname.includes('preview.emergentagent.com')
  ? (process.env.REACT_APP_BACKEND_URL || '')
  : '';

const PastelLeaf = ({ color = "#C8F4D6", style }) => (
  <svg viewBox="0 0 24 24" fill="none" style={style} className="leaf">
    <path d="M17,8C8,10 5.9,16.17 3.82,21.34L5.71,22L6.66,19.7C7.14,19.87 7.64,20 8,20C19,20 22,3 22,3C21,5 14,5.25 9,6.25C4,7.25 2,11.5 2,13.5C2,15.5 3.75,17.25 3.75,17.25C7,8 17,8 17,8Z"
      fill={color} stroke="#1A1A1A" strokeWidth="1"/>
  </svg>
);

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { darkMode, currentColors } = useTheme();
  const { t } = useLanguage();
  const navigate = useNavigate();

  // Admin login state
  const [adminMode, setAdminMode] = useState(false);
  const [adminCode, setAdminCode] = useState('');
  const [adminError, setAdminError] = useState('');
  const [adminLoading, setAdminLoading] = useState(false);

  // Forgot password state
  const [forgotMode, setForgotMode] = useState(null); // null | 'email' | 'code' | 'newpw' | 'done'
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [forgotMsg, setForgotMsg] = useState('');
  const [forgotError, setForgotError] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);

  const [c1, c2, c3] = currentColors;
  const leafColors = [c2, c3, c1, c3, c2];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Failed to login');
    } finally {
      setLoading(false);
    }
  };

  const handleSendCode = async (e) => {
    e.preventDefault();
    setForgotError('');
    setForgotLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/forgot-password`, { email: resetEmail });
      setForgotMsg(res.data.dev_code || '');
      setForgotMode('code');
    } catch (err) {
      setForgotError(err.response?.data?.detail || 'Something went wrong');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    if (resetCode.length !== 5) { setForgotError('Please enter the 5-digit code'); return; }
    setForgotError('');
    setForgotMode('newpw');
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) { setForgotError('Password must be at least 6 characters'); return; }
    setForgotError('');
    setForgotLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        email: resetEmail,
        code: resetCode,
        new_password: newPassword,
      });
      setForgotMode('done');
    } catch (err) {
      setForgotError(err.response?.data?.detail || 'Reset failed');
    } finally {
      setForgotLoading(false);
    }
  };

  const resetForgotState = () => {
    setForgotMode(null);
    setResetEmail('');
    setResetCode('');
    setNewPassword('');
    setForgotMsg('');
    setForgotError('');
  };

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setAdminError('');
    setAdminLoading(true);
    try {
      await axios.post(`${API_URL}/api/admin/login`, { code: adminCode }, { withCredentials: true });
      navigate('/admin');
    } catch (err) {
      setAdminError(err.response?.data?.detail || 'Something went wrong');
    } finally {
      setAdminLoading(false);
    }
  };

  return (
    <div className="min-h-screen auth-bg flex items-center justify-center p-4 relative">
      {/* Falling leaves */}
      {[...Array(10)].map((_, i) => (
        <PastelLeaf 
          key={i} 
          color={leafColors[i % leafColors.length]}
        />
      ))}

      {/* Settings gear */}
      <SettingsGear />

      {/* Login card */}
      <div className="w-full max-w-md soft-card p-8 sm:p-10 relative z-20" data-testid="login-card">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-block mb-2">
            <img src="https://customer-assets.emergentagent.com/job_mood-diary-calendar/artifacts/sd96v0rx_image.png" alt="Willow Notes" className="w-[36rem] object-contain mx-auto" />
          </div>
          <p className="text-[#4A5568] mt-2 font-medium">{t('welcomeBack')}</p>
        </div>

        {error && !forgotMode && !adminMode && (
          <div className="bg-[var(--c1)] border-[1.5px] border-[var(--c1-accent)] text-[#1A1A1A] px-4 py-3 rounded-full mb-6 shadow-sm" data-testid="login-error">
            {error}
          </div>
        )}

        {/* Admin Login Mode */}
        {adminMode ? (
          <div className="space-y-5">
            {adminError && (
              <div className="bg-red-50 border-[1.5px] border-red-200 text-red-700 px-4 py-3 rounded-2xl text-sm shadow-sm" data-testid="admin-login-error">
                {adminError}
              </div>
            )}
            <form onSubmit={handleAdminLogin} className="space-y-5">
              <p className="text-xs font-semibold text-[#9CA3AF] uppercase tracking-wide flex items-center gap-1">
                <Shield className="w-3 h-3" /> Admin Access
              </p>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4A5568]" />
                <Input
                  type="password"
                  value={adminCode}
                  onChange={(e) => setAdminCode(e.target.value)}
                  placeholder="Enter admin code"
                  required
                  className="pl-12 h-14 rounded-full border-[1.5px] border-[var(--border-soft)] bg-[var(--page-bg)] focus:border-[var(--c3-hover)] focus:ring-2 focus:ring-[var(--c3-30)] transition-all"
                  data-testid="admin-code-input"
                />
              </div>
              <Button
                type="submit"
                disabled={adminLoading}
                className="w-full h-14 soft-btn bg-[#1A1A1A] hover:bg-[#333] text-white text-xl"
                data-testid="admin-login-btn"
              >
                {adminLoading ? 'Verifying...' : (
                  <span className="flex items-center gap-2">
                    <Shield className="w-5 h-5" /> Enter Admin Panel
                  </span>
                )}
              </Button>
              <Button
                type="button"
                onClick={() => { setAdminMode(false); setAdminCode(''); setAdminError(''); }}
                className="w-full h-12 soft-btn bg-[var(--c1)] hover:bg-[var(--c1-hover)] text-[#1A1A1A]"
                data-testid="admin-back-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to Login
              </Button>
            </form>
          </div>
        ) : forgotMode ? (
          <div className="space-y-5">
            {forgotError && (
              <div className="bg-[var(--c1)] border-[1.5px] border-[var(--c1-accent)] text-[#1A1A1A] px-4 py-3 rounded-2xl text-sm shadow-sm" data-testid="forgot-error">
                {forgotError}
              </div>
            )}

            {forgotMode === 'email' && (
              <form onSubmit={handleSendCode} className="space-y-4">
                <p className="text-sm text-[#4A5568] text-center">Enter your email and we'll send you a 5-digit reset code</p>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4A5568]" />
                  <Input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    className="pl-12 h-14 rounded-full border-[1.5px] border-[var(--border-soft)] bg-[var(--page-bg)] focus:border-[var(--c3-hover)] focus:ring-2 focus:ring-[var(--c3-30)] transition-all"
                    data-testid="forgot-email-input"
                  />
                </div>
                <Button type="submit" disabled={forgotLoading} className="w-full h-12 soft-btn bg-[var(--c3)] hover:bg-[var(--c3-hover)] text-[#1A1A1A]" data-testid="forgot-send-code-btn">
                  {forgotLoading ? 'Sending...' : 'Send Reset Code'}
                </Button>
                <button type="button" onClick={resetForgotState} className="w-full text-sm text-[#4A5568] hover:text-[#1A1A1A] flex items-center justify-center gap-1 transition-colors" data-testid="forgot-back-btn">
                  <ArrowLeft className="w-4 h-4" /> Back to login
                </button>
              </form>
            )}

            {forgotMode === 'code' && (
              <form onSubmit={handleVerifyCode} className="space-y-4">
                <p className="text-sm text-[#4A5568] text-center">Enter the 5-digit code sent to <span className="font-semibold">{resetEmail}</span></p>
                {forgotMsg && (
                  <div className="bg-[var(--c2-40)] border-[1.5px] border-[var(--c2)] rounded-2xl px-4 py-3 text-center" data-testid="dev-code-hint">
                    <p className="text-xs text-[#6B7280] mb-1">Your reset code:</p>
                    <p className="text-2xl font-bold tracking-[0.3em] text-[#1A1A1A]">{forgotMsg}</p>
                  </div>
                )}
                <div className="relative">
                  <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4A5568]" />
                  <Input
                    type="text"
                    value={resetCode}
                    onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
                    placeholder="5-digit code"
                    maxLength={5}
                    className="pl-12 h-14 rounded-full border-[1.5px] border-[var(--border-soft)] bg-[var(--page-bg)] focus:border-[var(--c3-hover)] focus:ring-2 focus:ring-[var(--c3-30)] transition-all text-center text-2xl tracking-[0.5em] font-bold"
                    data-testid="forgot-code-input"
                  />
                </div>
                <Button type="submit" className="w-full h-12 soft-btn bg-[var(--c3)] hover:bg-[var(--c3-hover)] text-[#1A1A1A]" data-testid="forgot-verify-btn">
                  Verify Code
                </Button>
                <button type="button" onClick={() => setForgotMode('email')} className="w-full text-sm text-[#4A5568] hover:text-[#1A1A1A] flex items-center justify-center gap-1 transition-colors">
                  <ArrowLeft className="w-4 h-4" /> Different email
                </button>
              </form>
            )}

            {forgotMode === 'newpw' && (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <p className="text-sm text-[#4A5568] text-center">Choose your new password</p>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4A5568]" />
                  <Input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="New password (min 6 chars)"
                    required
                    className="pl-12 h-14 rounded-full border-[1.5px] border-[var(--border-soft)] bg-[var(--page-bg)] focus:border-[var(--c3-hover)] focus:ring-2 focus:ring-[var(--c3-30)] transition-all"
                    data-testid="forgot-new-password-input"
                  />
                </div>
                <Button type="submit" disabled={forgotLoading} className="w-full h-12 soft-btn bg-[var(--c1)] hover:bg-[var(--c1-hover)] text-[#1A1A1A]" data-testid="forgot-reset-btn">
                  {forgotLoading ? 'Resetting...' : 'Reset Password'}
                </Button>
              </form>
            )}

            {forgotMode === 'done' && (
              <div className="text-center space-y-4 py-4">
                <div className="text-4xl">&#127793;</div>
                <p className="text-lg font-bold text-[#1A1A1A]">Password reset!</p>
                <p className="text-sm text-[#4A5568]">You can now log in with your new password</p>
                <Button onClick={resetForgotState} className="w-full h-12 soft-btn bg-[var(--c1)] hover:bg-[var(--c1-hover)] text-[#1A1A1A]" data-testid="forgot-done-btn">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Back to Login
                </Button>
              </div>
            )}
          </div>
        ) : (
          /* Normal Login Form */
          <>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="email" className="text-[#1A1A1A] font-semibold mb-2 block">{t('email')}</Label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4A5568]" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    className="pl-12 h-14 rounded-full border-[1.5px] border-[var(--border-soft)] bg-[var(--page-bg)] focus:border-[var(--c3-hover)] focus:ring-2 focus:ring-[var(--c3-30)] transition-all"
                    data-testid="login-email-input"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label htmlFor="password" className="text-[#1A1A1A] font-semibold">{t('password')}</Label>
                  <button
                    type="button"
                    onClick={() => { setForgotMode('email'); setResetEmail(email); setForgotError(''); }}
                    className="text-xs text-[var(--c3-hover)] hover:text-[#1A1A1A] font-medium transition-colors"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </button>
                </div>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4A5568]" />
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Your secret password"
                    required
                    className="pl-12 h-14 rounded-full border-[1.5px] border-[var(--border-soft)] bg-[var(--page-bg)] focus:border-[var(--c3-hover)] focus:ring-2 focus:ring-[var(--c3-30)] transition-all"
                    data-testid="login-password-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-14 soft-btn bg-[var(--c1)] hover:bg-[var(--c1-hover)] text-[#1A1A1A] text-xl"
                data-testid="login-submit-button"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Signing in...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    {t('login')} <ArrowRight className="w-5 h-5" />
                  </span>
                )}
              </Button>
            </form>

            <p className="text-center mt-8 text-[#4A5568]">
              {t('noAccount')}{' '}
              <Link to="/register" className="text-[#1A1A1A] font-bold underline decoration-[var(--c3)] decoration-4 hover:decoration-[var(--c1)] transition-colors" data-testid="register-link">
                {t('signUp')}
              </Link>
            </p>
            <button
              type="button"
              onClick={() => { setAdminMode(true); setError(''); }}
              className="w-full mt-4 flex items-center justify-center gap-1.5 text-xs text-[#9CA3AF] hover:text-[#1A1A1A] transition-colors"
              data-testid="admin-login-link"
            >
              <Shield className="w-3 h-3" /> Admin Login
            </button>
          </>
        )}
      </div>
    </div>
  );
}
